import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";
import type { ProblemDetails } from "@/lib/problemDetails";

type BankTransaction = components["schemas"]["BankTransactionResponse"];

/** Decimals cross the wire as strings on some routes and numbers on others, so both are handled. */
function formatMoney(amount: string | number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined) return "-";
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return "-";
  return `${value.toFixed(2)} ${currency ?? ""}`.trim();
}

/** Admin route: shown whatever the connection's state, unlike the tenant route. */
export function BankAccountTransactionsTable({ organisationId, bankAccountId }: { organisationId: string; bankAccountId: string }) {
  const pagination = useCursorPagination({ initialLimit: 50 });
  const { setPage } = pagination;
  const [rows, setRows] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<ProblemDetails | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProblem(null);
    api
      .GET("/admin/organisations/{organisationId}/bank-accounts/{bankAccountId}/transactions", {
        params: { path: { organisationId, bankAccountId }, query: { Cursor: pagination.cursor, Limit: pagination.limit } },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setProblem(error as ProblemDetails);
          setRows([]);
        } else {
          setRows(data?.data ?? []);
          setPage({ nextCursor: data?.meta?.cursors?.next, previousCursor: data?.meta?.cursors?.previous });
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId, bankAccountId, pagination.cursor, pagination.limit, setPage]);

  if (loading && rows.length === 0 && !problem) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ProblemDetailsAlert problem={problem} fallback="Failed to load transactions." />
      {!problem && rows.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">No transactions synced for this account.</p>
      ) : rows.length > 0 ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Booked</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Counterparty</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
                <TableHead>Provider id</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="whitespace-nowrap">{formatDateTime(t.bookedAt)}</TableCell>
                  <TableCell>
                    <Badge variant={t.status === "Pending" ? "secondary" : "outline"}>{t.status}</Badge>
                  </TableCell>
                  <TableCell>{t.counterpartyName ?? "-"}</TableCell>
                  <TableCell className="max-w-64 truncate">{t.description ?? "-"}</TableCell>
                  <TableCell className="max-w-40 truncate font-mono text-xs">{t.reference ?? "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(t.amount, t.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(t.balanceAfter, t.currency)}</TableCell>
                  <TableCell className="max-w-40 truncate font-mono text-xs">{t.providerTransactionId ?? "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationBar
            hasPrevious={pagination.hasPrevious}
            hasNext={pagination.hasMore}
            isLoading={loading}
            limit={pagination.limit}
            onPrevious={pagination.goPrevious}
            onNext={pagination.goNext}
            onLimitChange={pagination.setLimit}
          />
        </>
      ) : null}
    </div>
  );
}
